import {db} from "../data/db";

// Editor drafts live in IndexedDB: pasted images are inlined as base64, and in localStorage a few abandoned
// drafts filled the ~5 MB quota, after which every other localStorage write (filters, settings) failed silently.
// localStorage is only a fallback when IndexedDB is unavailable and a synchronous write target on page hide.

const DRAFT_KEY_PREFIX = "react-quill-isolated-";
const DRAFT_KEY_SUFFIX = "-last-message";
export const EDITOR_DRAFT_MAX_AGE_DAYS = 30;

const isDraftStorageKey = (key: string) =>
    key.startsWith(DRAFT_KEY_PREFIX) && key.endsWith(DRAFT_KEY_SUFFIX);

const localGet = (key: string) => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const localSet = (key: string, content: string) => {
    try {
        localStorage.setItem(key, content);
        return true;
    } catch {
        return false;
    }
};

const localRemove = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch {
        // Draft persistence must never interrupt editing.
    }
};

const moveLocalDraftsToDb = async () => {
    if (!db) return;

    const keys: string[] = [];
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && isDraftStorageKey(key)) keys.push(key);
        }
    } catch {
        return;
    }

    for (const key of keys) {
        const content = localGet(key);
        if (content) {
            await db.editorDrafts.put({key, content, savedAt: new Date()});
        }
        localRemove(key);
    }
};

const removeExpiredDrafts = async () => {
    if (!db) return;
    const cutoff = new Date(Date.now() - EDITOR_DRAFT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
    await db.editorDrafts.where("savedAt").below(cutoff).delete();
};

let ready: Promise<boolean> | undefined;

// Moves drafts left in localStorage (older versions, page-hide writes) into IndexedDB and drops expired ones.
// Resolves to false when IndexedDB cannot be used, in which case drafts stay in localStorage.
export const prepareEditorDrafts = () => {
    if (!ready) {
        ready = (async () => {
            if (!db) return false;
            try {
                await moveLocalDraftsToDb();
                await removeExpiredDrafts();
                return true;
            } catch (e) {
                console.warn("Editor drafts stay in localStorage", e);
                return false;
            }
        })();
    }
    return ready;
};

export const readEditorDraft = async (key: string): Promise<string | null> => {
    if (await prepareEditorDrafts()) {
        try {
            // A page-hide write may have landed in localStorage after startup.
            const local = localGet(key);
            if (local !== null) {
                await db!.editorDrafts.put({key, content: local, savedAt: new Date()});
                localRemove(key);
                return local;
            }
            return (await db!.editorDrafts.get(key))?.content ?? null;
        } catch {
            // Fall through to localStorage.
        }
    }
    return localGet(key);
};

export const writeEditorDraft = async (key: string, content: string) => {
    if (await prepareEditorDrafts()) {
        try {
            await db!.editorDrafts.put({key, content, savedAt: new Date()});
            localRemove(key);
            return;
        } catch {
            // Fall through to localStorage.
        }
    }
    localSet(key, content);
};

// IndexedDB writes are not guaranteed to finish while the page unloads, so the last edit goes to localStorage
// synchronously and is moved into IndexedDB on the next read or startup.
export const writeEditorDraftBeforeUnload = (key: string, content: string) => {
    if (!localSet(key, content)) {
        void writeEditorDraft(key, content);
    }
};

export const removeEditorDraft = (key: string) => {
    localRemove(key);
    void prepareEditorDrafts().then(async (useDb) => {
        if (!useDb) return;
        try {
            await db!.editorDrafts.delete(key);
        } catch {
            // Draft persistence must never interrupt editing.
        }
    });
};
